import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type Column,
  createDateRangeFilterValidators,
  DateRenderer,
  EmptyStation,
  type ExplorerDataProvider,
  filterToPostGraphileFilter,
  FilterTypes,
  MessageBar,
  NavigationExplorer,
  sortToPostGraphileOrderBy,
  transformRange,
} from '@axinom/mosaic-ui';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  type EmailWhitelistPatternFilter,
  EmailWhitelistPatternsDocument,
  EmailWhitelistPatternsOrderBy,
  type EmailWhitelistPatternsQuery,
  type EmailWhitelistPatternsQueryVariables,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';

type EmailWhitelistPatternData = NonNullable<
  EmailWhitelistPatternsQuery['filtered']
>['nodes'][number];

const explorerColumns: Column<EmailWhitelistPatternData>[] = [
  { label: 'Email Whitelist Pattern', propertyName: 'emailPattern' },
  { label: 'Created At', propertyName: 'createdAt', render: DateRenderer },
  { label: 'Created By', propertyName: 'createdBy' },
  {
    label: 'Last Modified At',
    propertyName: 'updatedAt',
    render: DateRenderer,
  },
  { label: 'Last Modified By', propertyName: 'updatedBy' },
];

export const EmailWhitelistPatterns: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const apolloClient = useApolloClient();

  // Check the status of id-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.ID_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const dataProvider: ExplorerDataProvider<EmailWhitelistPatternData> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      // Get email whitelist patterns
      const emailWhitelistPatterns = await apolloClient.query<
        EmailWhitelistPatternsQuery,
        EmailWhitelistPatternsQueryVariables
      >({
        query: EmailWhitelistPatternsDocument,
        variables: {
          condition: {
            environmentId: envId,
          },
          filter: filterToPostGraphileFilter<EmailWhitelistPatternFilter>(
            filters,
            {
              emailPattern: 'includes',
              createdBy: 'includes',
              updatedBy: 'includes',
              createdAt: transformRange,
              updatedAt: transformRange,
            },
          ),
          orderBy: sortToPostGraphileOrderBy(
            sorting,
            EmailWhitelistPatternsOrderBy,
          ),
          after: pagingInformation,
        },
        fetchPolicy: 'network-only',
      });

      const data = emailWhitelistPatterns?.data.filtered?.nodes ?? [];

      return {
        data,
        totalCount: emailWhitelistPatterns?.data.nonFiltered?.totalCount ?? 0,
        filteredCount: emailWhitelistPatterns?.data.filtered?.totalCount ?? 0,
        hasMoreData:
          emailWhitelistPatterns?.data.filtered?.pageInfo.hasNextPage ?? false,
        pagingInformation:
          emailWhitelistPatterns?.data.filtered?.pageInfo.endCursor,
      };
    },
  };

  const [createFromDateFilterValidator, createToDateFilterValidator] =
    createDateRangeFilterValidators<EmailWhitelistPatternData>();

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Email Whitelist Patterns">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <NavigationExplorer<EmailWhitelistPatternData>
          title="Email Whitelist Patterns"
          stationKey="EmailWhitelistPatternsExplorer"
          columns={explorerColumns}
          dataProvider={dataProvider}
          calculateNavigateUrl={(item) => {
            return `/env/${envId}/services/${ManagedServiceId.ID_SERVICE}/config/emailWhitelistPatterns/${item.id}`;
          }}
          onCreateAction={`/env/${envId}/services/${ManagedServiceId.ID_SERVICE}/config/emailWhitelistPatterns/create`}
          defaultSortOrder={{ column: 'updatedAt', direction: 'desc' }}
          filterOptions={[
            {
              label: 'Email Whitelist Pattern',
              property: 'emailPattern',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Creation Period (From)',
              property: 'createdAt',
              type: FilterTypes.Date,
              onValidate: createFromDateFilterValidator('createdAt'),
            },
            {
              label: 'Creation Period (To)',
              property: 'createdAt',
              type: FilterTypes.Date,
              onValidate: createToDateFilterValidator('createdAt'),
            },
            {
              label: 'Created By',
              property: 'createdBy',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Modification Period (From)',
              property: 'updatedAt',
              type: FilterTypes.Date,
              onValidate: createFromDateFilterValidator('updatedAt'),
            },
            {
              label: 'Modification Period (To)',
              property: 'updatedAt',
              type: FilterTypes.Date,
              onValidate: createToDateFilterValidator('updatedAt'),
            },
            {
              label: 'Last Modified By',
              property: 'updatedBy',
              type: FilterTypes.FreeText,
            },
          ]}
        />
      )}
    </>
  );
};

export const EmailWhitelistPatternsCrumb: BreadcrumbResolver = () =>
  'Email Whitelist Patterns';
